import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X, Phone } from 'lucide-react';
import ApplicationForm from './ApplicationForm';
import ContactModal from './ContactModal';

const serviceItems = [
  'SOP & LOR Writing',
  'Visa Guidance',
  'Mock Interviews',
  'Post-landing Services',
  'Flight Ticket Booking'
];

const programItems = [
  { label: 'MBBS Abroad', to: '/mbbs-abroad' },
  { label: 'PhD Admissions', to: '/phd-admissions' },
  { label: 'Nursing in Germany', to: '/nursing-in-germany' },
  { label: 'Workforce Management', to: '/workforce-management' }
];

const Navbar = () => {
  const [servicesOpen, setServicesOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [applyFormOpen, setApplyFormOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactUsOpen, setContactUsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const servicesRef = useRef(null);
  const programsRef = useRef(null);

  // Frosted-glass navbar once user scrolls
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) setServicesOpen(false);
      if (programsRef.current && !programsRef.current.contains(event.target)) setProgramsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToServices = (e) => {
    setServicesOpen(false);
    setMobileMenuOpen(false);
    const element = document.getElementById('services');
    if (element) {
      e.preventDefault();
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const linkClass =
    'px-3.5 py-2 rounded-full text-sm font-semibold text-navy-900 hover:text-brand-600 hover:bg-white/80 transition-colors duration-200 inline-flex items-center gap-1.5';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/85 backdrop-blur-xl shadow-soft border-b border-navy-100/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-20' : 'h-24'}`}>
            {/* Brand */}
            <Link to="/" className="flex items-center group shrink-0" aria-label="Xpert Advisor home">
              <img
                src="/Logo.png"
                alt="Xpert Advisor — Rise Above Limits"
                className={`w-auto transition-all duration-300 group-hover:scale-105 ${scrolled ? 'h-14' : 'h-20'}`}
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              <Link to="/" className={linkClass}>Home</Link>
              <Link to="/universities" className={linkClass}>Universities</Link>

              <div className="relative" ref={servicesRef}>
                <button className={linkClass} onClick={() => { setServicesOpen(!servicesOpen); setProgramsOpen(false); }}>
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-3 w-64 bg-white rounded-2xl shadow-lift border border-navy-100 py-2.5 z-50 animate-fade-up [animation-duration:0.25s]">
                    {serviceItems.map((item) => (
                      <a
                        key={item}
                        href="/#services"
                        onClick={scrollToServices}
                        className="flex items-center gap-2.5 px-5 py-2.5 text-sm font-medium text-navy-800 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-400"></span>
                        {item}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative" ref={programsRef}>
                <button className={linkClass} onClick={() => { setProgramsOpen(!programsOpen); setServicesOpen(false); }}>
                  Programs
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${programsOpen ? 'rotate-180' : ''}`} />
                </button>
                {programsOpen && (
                  <div className="absolute top-full left-0 mt-3 w-64 bg-white rounded-2xl shadow-lift border border-navy-100 py-2.5 z-50 animate-fade-up [animation-duration:0.25s]">
                    {programItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setProgramsOpen(false)}
                        className="flex items-center gap-2.5 px-5 py-2.5 text-sm font-medium text-navy-800 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-navy-400"></span>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a href="/#loan-calculator" className={linkClass}>Loan Calculator</a>
            </div>

            {/* Right side CTAs */}
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setContactUsOpen(true)}
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-navy-900 border border-navy-200 bg-white/70 backdrop-blur-sm hover:border-navy-900 transition-all duration-200"
              >
                <Phone className="w-4 h-4" />
                Contact
              </button>
              <button
                onClick={() => setApplyFormOpen(true)}
                className="inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 shadow-brand-glow transition-all duration-200 hover:-translate-y-0.5"
              >
                Apply Now
              </button>
              {/* Mobile menu toggle */}
              <button
                className="lg:hidden w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm border border-navy-100 flex items-center justify-center text-navy-900"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl shadow-lift border-t border-navy-100 animate-fade-up [animation-duration:0.25s]">
            <div className="px-5 py-5 space-y-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl font-semibold text-navy-900 hover:bg-navy-50">Home</Link>
              <Link to="/universities" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl font-semibold text-navy-900 hover:bg-navy-50">Universities by Country</Link>
              <a href="/#services" onClick={scrollToServices} className="block px-4 py-3 rounded-xl font-semibold text-navy-900 hover:bg-navy-50">Services</a>
              <a href="/#loan-calculator" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl font-semibold text-navy-900 hover:bg-navy-50">Loan Calculator</a>

              <p className="px-4 pt-4 pb-1 text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">Programs</p>
              {programItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 rounded-xl font-semibold text-navy-900 hover:bg-navy-50"
                >
                  {item.label}
                </Link>
              ))}

              <div className="pt-4 flex gap-3">
                <button
                  onClick={() => { setMobileMenuOpen(false); setContactUsOpen(true); }}
                  className="flex-1 btn-outline-navy py-3"
                >
                  Contact Us
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); setApplyFormOpen(true); }}
                  className="flex-1 btn-primary py-3"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <ApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />
      <ContactModal open={contactUsOpen} onOpenChange={setContactUsOpen} />
    </>
  );
};

export default Navbar;
